import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { login } from "@/lib/api";
import { Lock, ArrowRight, Eye, EyeOff, Mail } from "lucide-react";

export default function Login() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await login(code.trim());
      toast.success("Welcome — opening the deck");
      nav("/", { replace: true });
    } catch (err) {
      toast.error("Invalid access code");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden text-ink-900 grain"
      data-testid="login-page"
      style={{ background: "linear-gradient(135deg, #FDFBF5 0%, #FBF4DC 100%)" }}
    >
      {/* Soft brand glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full blur-3xl opacity-40"
           style={{ background: "radial-gradient(circle, #F5B400 0%, #FFE070 40%, transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full blur-3xl opacity-25"
           style={{ background: "radial-gradient(circle, #FF7B1F 0%, transparent 70%)" }} />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-8 md:px-14 py-7">
        <div className="flex items-center gap-3" data-testid="brand-mark">
          <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-11 w-11 object-contain" />
          <span className="serif text-xl tracking-tight">CompliSun</span>
          <span className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/45 ml-2">Partner Deck</span>
        </div>
        <a
          href="https://complisunguardian.com"
          target="_blank"
          rel="noreferrer"
          className="mono text-[11px] uppercase tracking-[0.18em] text-ink-900/55 hover:text-sun-700 transition-colors"
        >
          complisunguardian.com →
        </a>
      </header>

      <main className="relative z-10 px-6 md:px-14 pt-16 md:pt-24 max-w-5xl">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-6 animate-fade-in">
          / private · access required
        </p>
        <h1 className="serif font-light text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tight animate-fade-in-up">
          A closer look at <em className="text-sun-600 font-normal">CompliSun</em> —
          <br className="hidden md:block" />
          built for the regulated.
        </h1>
        <p className="mt-8 max-w-2xl text-ink-900/70 text-lg leading-relaxed animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
          You've been invited to a private pitch experience. This deck walks through how we
          help fintechs ship faster without losing compliance — and how our closed-system AI
          architecture keeps client data out of training pipelines.
        </p>

        <form onSubmit={onSubmit} className="mt-14 max-w-md animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <label htmlFor="code" className="mono text-[11px] uppercase tracking-[0.22em] text-ink-900/55 flex items-center gap-2 mb-3">
            <Lock className="h-3 w-3" /> Access code
          </label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Input
                id="code"
                data-testid="access-code-input"
                type={show ? "text" : "password"}
                autoFocus
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter partner code"
                className="h-14 pr-12 bg-white/70 border-ink-900/15 text-ink-900 placeholder:text-ink-900/35 text-base focus-visible:ring-sun-500 focus-visible:border-sun-500"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                data-testid="access-code-toggle"
                aria-label={show ? "Hide code" : "Show code"}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded text-ink-900/55 hover:text-sun-700 hover:bg-sun-500/10 transition-colors"
                tabIndex={-1}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              type="submit"
              disabled={busy || !code.trim()}
              data-testid="access-code-submit"
              className="h-14 px-7 rounded-md bg-ink-900 hover:bg-ink-800 text-cream font-medium transition-all disabled:opacity-40"
            >
              {busy ? "Checking…" : (
                <span className="flex items-center gap-2">Enter <ArrowRight className="h-4 w-4" /></span>
              )}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/40">
              Code-protected. No account needed.
            </p>
            <a
              href="mailto:colindarke@complisun.com?subject=CompliSun%20access%20code%20request&body=Hi%20Colin%2C%0A%0AI%20forgot%20my%20CompliSun%20access%20code%20and%20would%20like%20to%20view%20the%20partner%20deck.%0A%0AThanks"
              data-testid="forgot-code-link"
              className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.18em] text-ink-900/55 hover:text-sun-700 transition-colors"
            >
              <Mail className="h-3 w-3" />
              Forgot the code? Email Colin
            </a>
          </div>
        </form>

        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl border-t border-ink-900/10 pt-10 animate-fade-in items-center" style={{ animationDelay: "0.45s" }}>
          <a
            href="https://www.lendapi.com/blog/lendapi-names-complisun-partner-of-the-year-2025-compliance-category"
            target="_blank"
            rel="noreferrer"
            data-testid="login-lendapi-award"
            className="flex items-start gap-4 group"
          >
            <img src="/brand/platform/award.jpg" alt="LendAPI Partner of the Year 2025"
                 className="h-20 w-auto rounded-md shadow-sm" />
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.18em] text-sun-700">LendAPI · 2025</div>
              <div className="serif text-base text-ink-900 mt-0.5 group-hover:text-sun-700 transition-colors">
                Partner of the Year — Compliance
              </div>
              <div className="mono text-[10px] uppercase tracking-[0.18em] text-ink-900/45 mt-1 group-hover:text-sun-700/80">
                read the announcement →
              </div>
            </div>
          </a>
          <div className="md:col-span-2 md:pl-10">
            <div className="serif text-2xl md:text-3xl text-ink-900 leading-snug">
              Replace a <em className="text-sun-700">$280k</em> compliance-officer hire
              with a full fractional team for <em className="text-sun-700">$3,225 / month</em>.
            </div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mt-3">
              Platform + 20 hrs of fractional team · 45-day fully-functional rollout
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 mt-20 px-8 md:px-14 py-6 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/40">
        © {new Date().getFullYear()} · CompliSun · Compliance Simplified
      </footer>
    </div>
  );
}
